import flatpickr from "flatpickr"

// The one place a date picker is configured. Both bundles import this: marketing.js
// for the public site, which ships no Stimulus, and the datepicker Stimulus
// controller for the dashboard. A date field should look and behave the same on
// both sides, so the decisions live here rather than being written twice and
// drifting.
//
// Styling for the calendar itself lives in application.tailwind.css, which both
// layouts already load. It is not imported here: esbuild's CSS-from-JS handling
// is unreliable with this build setup.

// The canonical input recipe from DESIGN.md section 8. flatpickr replaces the real
// input with a display one of its own making, and without this the replacement
// arrives with none of the site's field styling.
const INPUT_CLASS =
  "mt-1.5 block w-full rounded-lg border border-line bg-paper px-4 py-3 " +
  "focus:outline-none focus:ring-2 focus:ring-book"

export const datePickerConfig = (overrides = {}) => ({
  // The field shows "May 1, 2021" while the form still posts 2021-05-01 from the
  // real input, which flatpickr keeps around hidden.
  altInput: true,
  altInputClass: INPUT_CLASS,
  altFormat: "F j, Y",
  dateFormat: "Y-m-d",

  // Not optional, and not about letting people type. Left off, flatpickr marks the
  // visible input readonly, and a readonly control is barred from HTML constraint
  // validation, so `required` on a date field silently stops being enforced: the
  // browser lets an empty one through and the public form's per-step gate cannot
  // see it either.
  allowInput: true,

  // One picker everywhere. At the default, flatpickr swaps in the operating
  // system's own control on a phone and hides both the real and the display
  // input, which undoes the label wiring below, drops the field to an unstyled
  // native box, and moves it to the front of the tab order.
  disableMobile: true,

  // flatpickr parses whatever is typed using altFormat alone, and silently empties
  // the field when that fails. So a parent who types 2021-05-01, or whose browser
  // autofills a birth date in that shape, watches their answer disappear with no
  // message. Accept the ISO form as well as the friendly one.
  //
  // The ISO branch is spelled out rather than handed to Date, because
  // `new Date("2021-05-01")` is parsed as UTC midnight and lands on April 30th in
  // every timezone west of Greenwich, this one included.
  parseDate: (value) => {
    const text = String(value).trim();
    const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(text);
    if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));

    const parsed = new Date(text);
    return isNaN(parsed.getTime()) ? undefined : parsed;
  },

  ...overrides
})

// flatpickr hides the real input and shows a new one, which leaves the field's
// `<label for>` pointing at something invisible. Clicking the label stops focusing
// anything, a screen reader loses the field's name, and Capybara can no longer
// find it by label. Point the label at the input a person actually sees, and carry
// the server-rendered error state across so a rejected field stays announced.
const adoptLabelAndErrorState = (input, altInput) => {
  if (input.id) {
    altInput.id = `${input.id}_display`
    document.querySelector(`label[for="${input.id}"]`)?.setAttribute("for", altInput.id)
  }

  ;[ "aria-invalid", "aria-describedby" ].forEach((name) => {
    const value = input.getAttribute(name)
    if (value) altInput.setAttribute(name, value)
  })
}

// A field's own bounds, declared on the field. Read here rather than in either
// bundle so both get them and neither has to remember: this file is the one place
// a picker is configured, and a cap that only marketing.js honoured would drift
// the moment the same field appeared on the dashboard.
//
// "today" is passed through as flatpickr's own keyword. A birth date carries
// data-cc-max-date="today", which stops a calendar that opens on the current month
// from paging into a future nobody can be born in.
//
// A bound never hides an answer already in the field. flatpickr drops a preset
// value outside its range without a word, so a resumed draft whose start date has
// since passed, or a rejected submission handing a date back, would come back
// with the field empty and the parent told "Please fill out this field" about a
// date they chose. The bound is widened to take that value in; the model's own
// message ("cannot be in the past") then explains it at the one moment it matters.
const atMidnight = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate())

const boundDate = (bound) => {
  if (bound === "today") return atMidnight(new Date())
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(String(bound))
  return iso ? new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3])) : null
}

const boundsFrom = (input) => {
  const bounds = {}
  if (input.dataset.ccMinDate) bounds.minDate = input.dataset.ccMinDate
  if (input.dataset.ccMaxDate) bounds.maxDate = input.dataset.ccMaxDate

  const held = boundDate(input.value)
  if (!held) return bounds
  const min = bounds.minDate && boundDate(bounds.minDate)
  const max = bounds.maxDate && boundDate(bounds.maxDate)
  if (min && held < min) bounds.minDate = held
  if (max && held > max) bounds.maxDate = held
  return bounds
}

// What to say about a typed date flatpickr would not take. The bound half is built
// from the field's own config rather than written on the field, so a cap declared in
// one place cannot be described wrongly in another.
const rejectionMessage = (instance, text) => {
  const shown = (date) => instance.formatDate(date, instance.config.altFormat)
  const { minDate, maxDate } = instance.config

  if (!instance.config.parseDate(text)) return "Use a date like March 14, 2022."
  if (minDate && maxDate) return `Use a date between ${shown(minDate)} and ${shown(maxDate)}.`
  if (maxDate) return `Use a date on or before ${shown(maxDate)}.`
  if (minDate) return `Use a date on or after ${shown(minDate)}.`
  return "That date cannot be used here."
}

// flatpickr answers a value it will not take by emptying the field, and says nothing
// about it. A parent who typed 2028 meaning 2018 watched their answer disappear and
// was then told "Please fill out this field" about a field they had just filled.
//
// Put the answer back and say what is wrong with it. The message goes through
// setCustomValidity, so it arrives as validationMessage: the public form's per-step
// gate already renders that under the field, and anywhere else the browser's own
// bubble shows it, without either side being taught anything about dates. It is also
// what keeps the restored text from reading as a filled field, since a control with a
// custom validity fails checkValidity() however full it looks.
//
// This listener is bound after flatpickr's own, and listeners run in the order they
// were added, so by the time it sees the blur the parse has already happened and the
// verdict is on the instance. Deferring it to a timeout instead would open a race the
// spec caught: a parent who tabs away and immediately starts retyping would have the
// rejected text written back into the middle of their new answer.
const explainRejectedTyping = (instance) => {
  const display = instance.altInput
  if (!display) return

  let typed = ""

  display.addEventListener("input", () => {
    typed = display.value
    display.setCustomValidity("")
  })

  display.addEventListener("blur", () => {
    // Whether the picker took a date, not whether the box has text in it. The two
    // rejections do not look alike: an out-of-range date is wiped, while an
    // unparseable one is left sitting there, so a field reading "bananas" satisfied
    // its own required attribute, passed the step gate, and posted nothing. Asking
    // what the picker actually holds catches both the same way.
    if (instance.selectedDates.length > 0) return display.setCustomValidity("")
    if (typed.trim() === "") return

    display.value = typed
    display.setCustomValidity(rejectionMessage(instance, typed))
  })
}

export const attachDatePicker = (input, overrides = {}) => {
  const instance = flatpickr(input, datePickerConfig({ ...boundsFrom(input), ...overrides }))
  if (instance.altInput) adoptLabelAndErrorState(input, instance.altInput)
  explainRejectedTyping(instance)
  return instance
}
